/*
 * Subscription picker on the product page (snippets/ellio-subscription-picker.liquid,
 * block ellio_subscription_picker in sections/main-product.liquid).
 *
 * One request to /cart/add puts the device and, unless "device only" is
 * chosen, the subscription product with its selling plan in the cart. The
 * response goes to the cart drawer the way Dawn's product form hands it over.
 *
 * Which option's details, summary row and warning show follows data-selected
 * on this element; assets/ellio-subscription-picker.css hides the others.
 *
 * Dawn's quick add modal runs the scripts in its markup again, so the class is
 * defined inside the guard: a top-level class declaration would throw there.
 */
if (!customElements.get("ellio-subscription-picker")) {
  customElements.define(
    "ellio-subscription-picker",
    class EllioSubscriptionPicker extends HTMLElement {
      static SOURCE = "ellio-subscription-picker";

      // A /cart/add request is running.
      requestInFlight = false;
      // Sense is fetching the section for a changed variant; the device ID in
      // this markup is outdated until it publishes the result.
      awaitingVariant = false;

      connectedCallback() {
        this.addEventListener("change", this.onChange);
        this.addEventListener("submit", this.onSubmit);
        document.addEventListener("change", this.onDocumentChange);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);

        // The browser can restore a radio, or the shopper can click one, before
        // this script runs. The shown option and the cart fields follow it.
        const checked = this.querySelector('input[type="radio"]:checked');
        if (checked) this.select(checked);
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onChange);
        this.removeEventListener("submit", this.onSubmit);
        document.removeEventListener("change", this.onDocumentChange);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onChange = (event) => {
        if (event.target.matches('input[type="radio"]')) this.select(event.target);
      };

      // Sense disables its own button as soon as an option changes. The picker
      // button follows until the matching variantChange brings fresh markup.
      onDocumentChange = (event) => {
        const selects = event.target instanceof Element ? event.target.closest("variant-selects") : null;
        if (!selects || selects.dataset.section !== this.dataset.sectionId) return;
        this.awaitingVariant = true;
        this.refreshButton();
      };

      select(input) {
        this.dataset.selected = input.value;
        const withSubscription = input.value !== "none";
        this.querySelectorAll("[data-subscription-field]").forEach((field) => {
          field.disabled = !withSubscription;
        });
        if (!withSubscription) return;
        this.field("id").value = input.dataset.variantId;
        this.field("selling_plan").value = input.dataset.sellingPlan;
      }

      field(name) {
        return this.querySelector(`[data-subscription-field="${name}"]`);
      }

      radio(value) {
        return value ? this.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`) : null;
      }

      // One device and one subscription, as the summary and the form without
      // JavaScript show. The quantity selector belongs to the buy buttons.
      items() {
        const items = [{ id: Number(this.querySelector('[name="items[0][id]"]').value), quantity: 1 }];
        const chosen = this.querySelector('input[type="radio"]:checked');
        if (chosen && chosen.value !== "none") {
          items.push({ id: Number(chosen.dataset.variantId), quantity: 1, selling_plan: Number(chosen.dataset.sellingPlan) });
        }
        return items;
      }

      onSubmit = (event) => {
        event.preventDefault();
        const button = this.querySelector(".ellio-sp-submit");
        if (!button || button.disabled || this.requestInFlight || this.awaitingVariant) return;

        this.showError("");
        this.requestInFlight = true;
        this.refreshButton();

        const cart = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        const body = { items: this.items() };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(button);
        }

        fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: JSON.stringify(body) })
          .then((response) => response.json())
          .then(
            (response) => {
              if (response.status) {
                this.fail(response.description || response.message, body, response.description);
                return;
              }
              if (!cart) {
                this.redirectToCart();
                return;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: EllioSubscriptionPicker.SOURCE,
                productVariantId: body.items[0].id,
                cartData: response,
              });
              // Dawn's cart popup shows the line whose key it is given.
              const firstKey = response.items && response.items[0] && response.items[0].key;
              cart.renderContents(cart.tagName === "CART-NOTIFICATION" ? { ...response, key: firstKey } : response);
            },
            (error) => {
              console.error(error);
              this.fail("", body, "");
            },
          )
          // The add went through; a cart that fails to draw it is no reason to ask for another try.
          .catch((error) => console.error(error))
          .finally(() => {
            this.requestInFlight = false;
            this.refreshButton();
          });
      };

      redirectToCart() {
        window.location = routes.cart_url;
      }

      fail(message, body, shown) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: EllioSubscriptionPicker.SOURCE,
          productVariantId: body.items[0].id,
          errors: message,
          message,
        });
        this.showError(shown || this.dataset.errorFallback);
        this.syncCart();
      }

      // A refused or lost request can still have changed the cart: Shopify may
      // have taken the device before refusing the subscription, or the answer got
      // lost after the add went through. Cart count and drawer follow the server
      // again, so a second try does not add a second device unnoticed.
      syncCart() {
        const drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.refresh === "function") drawer.refresh();

        fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
          .then((response) => response.text())
          .then((markup) => {
            const bubble = document.getElementById("cart-icon-bubble");
            const source = new DOMParser().parseFromString(markup, "text/html").querySelector(".shopify-section");
            if (bubble && source) bubble.innerHTML = source.innerHTML;
          })
          .catch((error) => console.error(error));
      }

      showError(message) {
        const box = this.querySelector(".ellio-sp-error");
        if (!box) return;
        box.querySelector(".ellio-sp-error__text").textContent = message;
        box.hidden = !message;
      }

      // The state lives on the element, so fresh markup after a variant change
      // picks it up. A request in flight shows the busy look, a pending variant
      // only disables the button.
      refreshButton() {
        const button = this.querySelector(".ellio-sp-submit");
        if (!button) return;
        const spinner = button.querySelector(".ellio-sp-submit__spinner");
        const label = button.querySelector(".ellio-sp-submit__label");

        if (this.requestInFlight) {
          button.setAttribute("aria-busy", "true");
          if (spinner) spinner.hidden = false;
          if (label) label.textContent = this.dataset.addingLabel;
        } else if (button.getAttribute("aria-busy") === "true") {
          button.removeAttribute("aria-busy");
          if (spinner) spinner.hidden = true;
          if (label) label.textContent = this.dataset.addLabel;
        }

        if (this.requestInFlight || this.awaitingVariant) {
          button.setAttribute("aria-disabled", "true");
        } else {
          button.removeAttribute("aria-disabled");
        }
      }

      // Sense re-renders the section for the new variant and publishes its markup.
      // Taking this block from it brings the device's variant ID and stock state.
      onVariantChange = (event) => {
        const data = (event && event.data) || {};
        if (data.sectionId !== this.dataset.sectionId) return;
        this.awaitingVariant = false;
        const fresh = data.html ? data.html.getElementById(this.id) : null;
        if (!fresh) {
          this.refreshButton();
          return;
        }

        const chosen = this.dataset.selected;
        const focused = this.contains(document.activeElement) ? document.activeElement : null;
        const focusedValue = focused && focused.matches('input[type="radio"]') ? focused.value : null;

        const openDetails = [...this.querySelectorAll(".ellio-sp-details[data-option][open]")].map(
          (details) => details.dataset.option,
        );

        this.innerHTML = fresh.innerHTML;

        openDetails.forEach((option) => {
          const details = this.querySelector(`.ellio-sp-details[data-option="${CSS.escape(option)}"]`);
          if (details) details.open = true;
        });
        this.refreshButton();

        const input = this.radio(chosen) || this.querySelector('input[type="radio"]:checked');
        if (input) {
          input.checked = true;
          this.select(input);
        }
        const target = this.radio(focusedValue);
        if (target) target.focus();
      };
    },
  );
}
